import React, { useState, useEffect } from "react"
import { Button } from "@/components/ui/button" // Assuming Button path
import { User } from "@/api/users"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select" // Import Select components

export interface InformationFormData {
  // Individual fields
  firstName?: string;
  lastName?: string;
  contactEmail?: string; // Pre-filled, confirmation
  countryOfResidence?: string;
  purposeOfUse?: string;
  taxId?: string; // Optional

  // Business fields
  businessName?: string;
  businessEntityType?: string;
  contactName?: string; // For business
  // contactEmail is shared
  // countryOfResidence is shared
  // purposeOfUse is shared
  // taxId is shared (optional for business too)
  websiteUrl?: string; // Optional
}

// A sample list of countries.
const countries: { value: string; label: string }[] = [
  { value: "AD", label: "Andorra" },
  { value: "AE", label: "United Arab Emirates" },
  { value: "AF", label: "Afghanistan" },
  { value: "AG", label: "Antigua and Barbuda" },
  { value: "AI", label: "Anguilla" },
  { value: "AL", label: "Albania" },
  { value: "AM", label: "Armenia" },
  { value: "AO", label: "Angola" },
  { value: "AQ", label: "Antarctica" },
  { value: "AR", label: "Argentina" },
  { value: "AS", label: "American Samoa" },
  { value: "AT", label: "Austria" },
  { value: "AU", label: "Australia" },
  { value: "AW", label: "Aruba" },
  { value: "AX", label: "Åland Islands" },
  { value: "AZ", label: "Azerbaijan" },
  { value: "BA", label: "Bosnia and Herzegovina" },
  { value: "BB", label: "Barbados" },
  { value: "BD", label: "Bangladesh" },
  { value: "BE", label: "Belgium" },
  { value: "BF", label: "Burkina Faso" },
  { value: "BG", label: "Bulgaria" },
  { value: "BH", label: "Bahrain" },
  { value: "BI", label: "Burundi" },
  { value: "BJ", label: "Benin" },
  { value: "BL", label: "Saint Barthélemy" },
  { value: "BM", label: "Bermuda" },
  { value: "BN", label: "Brunei Darussalam" },
  { value: "BO", label: "Bolivia" },
  { value: "BQ", label: "Bonaire, Sint Eustatius and Saba" },
  { value: "BR", label: "Brazil" },
  { value: "BS", label: "Bahamas" },
  { value: "BT", label: "Bhutan" },
  { value: "BV", label: "Bouvet Island" },
  { value: "BW", label: "Botswana" },
  { value: "BY", label: "Belarus" },
  { value: "BZ", label: "Belize" },
  { value: "CA", label: "Canada" },
  { value: "CC", label: "Cocos (Keeling) Islands" },
  { value: "CD", label: "Democratic Republic of the Congo" },
  { value: "CF", label: "Central African Republic" },
  { value: "CG", label: "Congo" },
  { value: "CH", label: "Switzerland" },
  { value: "CI", label: "Côte d'Ivoire" },
  { value: "CK", label: "Cook Islands" },
  { value: "CL", label: "Chile" },
  { value: "CM", label: "Cameroon" },
  { value: "CN", label: "China" },
  { value: "CO", label: "Colombia" },
  { value: "CR", label: "Costa Rica" },
  { value: "CV", label: "Cabo Verde" },
  { value: "CW", label: "Curaçao" },
  { value: "CX", label: "Christmas Island" },
  { value: "CY", label: "Cyprus" },
  { value: "CZ", label: "Czechia" },
  { value: "DE", label: "Germany" },
  { value: "DJ", label: "Djibouti" },
  { value: "DK", label: "Denmark" },
  { value: "DM", label: "Dominica" },
  { value: "DO", label: "Dominican Republic" },
  { value: "DZ", label: "Algeria" },
  { value: "EC", label: "Ecuador" },
  { value: "EE", label: "Estonia" },
  { value: "EG", label: "Egypt" },
  { value: "EH", label: "Western Sahara" },
  { value: "ER", label: "Eritrea" },
  { value: "ES", label: "Spain" },
  { value: "ET", label: "Ethiopia" },
  { value: "FI", label: "Finland" },
  { value: "FJ", label: "Fiji" },
  { value: "FK", label: "Falkland Islands" },
  { value: "FM", label: "Micronesia" },
  { value: "FO", label: "Faroe Islands" },
  { value: "FR", label: "France" },
  { value: "GA", label: "Gabon" },
  { value: "GB", label: "United Kingdom" },
  { value: "GD", label: "Grenada" },
  { value: "GE", label: "Georgia" },
  { value: "GF", label: "French Guiana" },
  { value: "GG", label: "Guernsey" },
  { value: "GH", label: "Ghana" },
  { value: "GI", label: "Gibraltar" },
  { value: "GL", label: "Greenland" },
  { value: "GM", label: "Gambia" },
  { value: "GN", label: "Guinea" },
  { value: "GP", label: "Guadeloupe" },
  { value: "GQ", label: "Equatorial Guinea" },
  { value: "GR", label: "Greece" },
  { value: "GS", label: "South Georgia and the South Sandwich Islands" },
  { value: "GT", label: "Guatemala" },
  { value: "GU", label: "Guam" },
  { value: "GW", label: "Guinea-Bissau" },
  { value: "GY", label: "Guyana" },
  { value: "HK", label: "Hong Kong" },
  { value: "HM", label: "Heard Island and McDonald Islands" },
  { value: "HN", label: "Honduras" },
  { value: "HR", label: "Croatia" },
  { value: "HT", label: "Haiti" },
  { value: "HU", label: "Hungary" },
  { value: "ID", label: "Indonesia" },
  { value: "IE", label: "Ireland" },
  { value: "IL", label: "Israel" },
  { value: "IM", label: "Isle of Man" },
  { value: "IN", label: "India" },
  { value: "IO", label: "British Indian Ocean Territory" },
  { value: "IQ", label: "Iraq" },
  { value: "IS", label: "Iceland" },
  { value: "IT", label: "Italy" },
  { value: "JE", label: "Jersey" },
  { value: "JM", label: "Jamaica" },
  { value: "JO", label: "Jordan" },
  { value: "JP", label: "Japan" },
  { value: "KE", label: "Kenya" },
  { value: "KG", label: "Kyrgyzstan" },
  { value: "KH", label: "Cambodia" },
  { value: "KI", label: "Kiribati" },
  { value: "KM", label: "Comoros" },
  { value: "KN", label: "Saint Kitts and Nevis" },
  { value: "KR", label: "South Korea" },
  { value: "KW", label: "Kuwait" },
  { value: "KY", label: "Cayman Islands" },
  { value: "KZ", label: "Kazakhstan" },
  { value: "LA", label: "Laos" },
  { value: "LB", label: "Lebanon" },
  { value: "LC", label: "Saint Lucia" },
  { value: "LI", label: "Liechtenstein" },
  { value: "LK", label: "Sri Lanka" },
  { value: "LR", label: "Liberia" },
  { value: "LS", label: "Lesotho" },
  { value: "LT", label: "Lithuania" },
  { value: "LU", label: "Luxembourg" },
  { value: "LV", label: "Latvia" },
  { value: "LY", label: "Libya" },
  { value: "MA", label: "Morocco" },
  { value: "MC", label: "Monaco" },
  { value: "MD", label: "Moldova" },
  { value: "ME", label: "Montenegro" },
  { value: "MF", label: "Saint Martin" },
  { value: "MG", label: "Madagascar" },
  { value: "MH", label: "Marshall Islands" },
  { value: "MK", label: "North Macedonia" },
  { value: "ML", label: "Mali" },
  { value: "MM", label: "Myanmar" },
  { value: "MN", label: "Mongolia" },
  { value: "MO", label: "Macao" },
  { value: "MP", label: "Northern Mariana Islands" },
  { value: "MQ", label: "Martinique" },
  { value: "MR", label: "Mauritania" },
  { value: "MS", label: "Montserrat" },
  { value: "MT", label: "Malta" },
  { value: "MU", label: "Mauritius" },
  { value: "MV", label: "Maldives" },
  { value: "MW", label: "Malawi" },
  { value: "MX", label: "Mexico" },
  { value: "MY", label: "Malaysia" },
  { value: "MZ", label: "Mozambique" },
  { value: "NA", label: "Namibia" },
  { value: "NC", label: "New Caledonia" },
  { value: "NE", label: "Niger" },
  { value: "NF", label: "Norfolk Island" },
  { value: "NG", label: "Nigeria" },
  { value: "NI", label: "Nicaragua" },
  { value: "NL", label: "Netherlands" },
  { value: "NO", label: "Norway" },
  { value: "NP", label: "Nepal" },
  { value: "NR", label: "Nauru" },
  { value: "NU", label: "Niue" },
  { value: "NZ", label: "New Zealand" },
  { value: "OM", label: "Oman" },
  { value: "PA", label: "Panama" },
  { value: "PE", label: "Peru" },
  { value: "PF", label: "French Polynesia" },
  { value: "PG", label: "Papua New Guinea" },
  { value: "PH", label: "Philippines" },
  { value: "PK", label: "Pakistan" },
  { value: "PL", label: "Poland" },
  { value: "PM", label: "Saint Pierre and Miquelon" },
  { value: "PN", label: "Pitcairn Islands" },
  { value: "PR", label: "Puerto Rico" },
  { value: "PS", label: "Palestine" },
  { value: "PT", label: "Portugal" },
  { value: "PW", label: "Palau" },
  { value: "PY", label: "Paraguay" },
  { value: "QA", label: "Qatar" },
  { value: "RE", label: "Réunion" },
  { value: "RO", label: "Romania" },
  { value: "RS", label: "Serbia" },
  { value: "RW", label: "Rwanda" },
  { value: "SA", label: "Saudi Arabia" },
  { value: "SB", label: "Solomon Islands" },
  { value: "SC", label: "Seychelles" },
  { value: "SD", label: "Sudan" },
  { value: "SE", label: "Sweden" },
  { value: "SG", label: "Singapore" },
  { value: "SH", label: "Saint Helena, Ascension and Tristan da Cunha" },
  { value: "SI", label: "Slovenia" },
  { value: "SJ", label: "Svalbard and Jan Mayen" },
  { value: "SK", label: "Slovakia" },
  { value: "SL", label: "Sierra Leone" },
  { value: "SM", label: "San Marino" },
  { value: "SN", label: "Senegal" },
  { value: "SO", label: "Somalia" },
  { value: "SR", label: "Suriname" },
  { value: "SS", label: "South Sudan" },
  { value: "ST", label: "Sao Tome and Principe" },
  { value: "SV", label: "El Salvador" },
  { value: "SX", label: "Sint Maarten" },
  { value: "SZ", label: "Eswatini" },
  { value: "TC", label: "Turks and Caicos Islands" },
  { value: "TD", label: "Chad" },
  { value: "TF", label: "French Southern Territories" },
  { value: "TG", label: "Togo" },
  { value: "TH", label: "Thailand" },
  { value: "TJ", label: "Tajikistan" },
  { value: "TK", label: "Tokelau" },
  { value: "TL", label: "Timor-Leste" },
  { value: "TM", label: "Turkmenistan" },
  { value: "TN", label: "Tunisia" },
  { value: "TO", label: "Tonga" },
  { value: "TR", label: "Türkiye" },
  { value: "TT", label: "Trinidad and Tobago" },
  { value: "TV", label: "Tuvalu" },
  { value: "TW", label: "Taiwan" },
  { value: "TZ", label: "Tanzania" },
  { value: "UA", label: "Ukraine" },
  { value: "UG", label: "Uganda" },
  { value: "UM", label: "U.S. Outlying Islands" },
  { value: "US", label: "United States" },
  { value: "UY", label: "Uruguay" },
  { value: "UZ", label: "Uzbekistan" },
  { value: "VA", label: "Holy See" },
  { value: "VC", label: "Saint Vincent and the Grenadines" },
  { value: "VE", label: "Venezuela" },
  { value: "VG", label: "British Virgin Islands" },
  { value: "VI", label: "U.S. Virgin Islands" },
  { value: "VN", label: "Vietnam" },
  { value: "VU", label: "Vanuatu" },
  { value: "WF", label: "Wallis and Futuna" },
  { value: "WS", label: "Samoa" },
  { value: "YE", label: "Yemen" },
  { value: "YT", label: "Mayotte" },
  { value: "ZA", label: "South Africa" },
  { value: "ZM", label: "Zambia" },
  { value: "ZW", label: "Zimbabwe" },
]

const individualPurposeOfUseOptions: { value: string; label: string }[] = [
  { value: "freelance_services", label: "Freelance Services" },
  { value: "selling_products_online", label: "Selling Products Online" },
  { value: "client_invoicing", label: "Client Invoicing" },
  {
    value: "ecommerce_store_integration",
    label: "E-commerce Store Integration",
  },
  { value: "charity_donations", label: "Charity or Nonprofit Donations" },
  { value: "consulting_coaching", label: "Consulting or Coaching Services" },
  {
    value: "remittances_personal_transfers",
    label: "Remittances or Personal Transfers",
  },
]

// Assuming these are the business options, or a general default
const businessPurposeOfUseOptions: { value: string; label: string }[] = [
  { value: "accept_payments_clients", label: "Accept payments from clients" },
  { value: "ecommerce_sales", label: "E-commerce sales" },
  { value: "software_subscriptions", label: "Software subscriptions" },
  { value: "marketplace_payouts", label: "Marketplace payouts" },
  { value: "other", label: "Other" },
]

const businessEntityTypeOptions: { value: string; label: string }[] = [
  { value: "sole_proprietorship", label: "Sole Proprietorship" },
  { value: "partnership", label: "Partnership" },
  { value: "llc", label: "Limited Liability Company (LLC)" },
  { value: "corporation", label: "Corporation" },
  { value: "non_profit", label: "Non-profit Organization" },
  { value: "other", label: "Other" },
]

interface InformationFormProps {
  userType: "INDIVIDUAL" | "BUSINESS" | string;
  user: User;
  onNext: () => void;
  updateUserData: (data: {
    personalInfo?: Partial<InformationFormData>;
    businessInfo?: Partial<InformationFormData>;
    userType?: string;
  }) => void;
  saveOnboardingInfo: (data: {
    userType: string;
    personalInfo?: Partial<InformationFormData>;
    businessInfo?: Partial<InformationFormData>;
  }) => Promise<User>;
  isLoading?: boolean;
}

export default function InformationForm({
  userType,
  user,
  onNext,
  updateUserData,
  saveOnboardingInfo,
  isLoading,
}: InformationFormProps) {
  const [formData, setFormData] = useState<InformationFormData>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    // Pre-fill common data or type-specific data if available from user object
    // This assumes `user.contactEmail` might be the one to prefill from signup `user.email`
    let initialFormData: Partial<InformationFormData> = {
      contactEmail: user?.email || "",
    }

    if (userType === "INDIVIDUAL") {
      initialFormData = {
        ...initialFormData,
        firstName: user?.firstName || "",
        lastName: user?.lastName || "",
      }
    }
    // For BUSINESS userType, businessName and contactName are not on the base User object.
    // They will be populated into formData by user input and will persist in formData state.
    // If these fields were previously entered and saved, they would be part of the formData state already.

    setFormData((prev) => ({ ...prev, ...initialFormData }))
  }, [user, userType])

  // Log formData when it changes
  useEffect(() => {
    // console.log('[InformationForm] formData changed:', formData); // Removed console.log
  }, [formData])

  const handleSelectChange = (
    name: keyof InformationFormData,
    value: string,
  ) => {
    // console.log(`[InformationForm] handleSelectChange called. Name: ${name}, Value: ${value}`); // Removed console.log
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  // Original handleChange for text inputs, if any are left or for future use
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target
    // Ensure name is a key of InformationFormData before setting state
    if (
      name in formData ||
      Object.keys(formData).length === 0 ||
      [
        // list of all known keys in InformationFormData, can be generated or maintained
        "firstName",
        "lastName",
        "contactEmail",
        "countryOfResidence",
        "purposeOfUse",
        "taxId",
        "businessName",
        "businessEntityType",
        "contactName",
        "websiteUrl",
      ].includes(name)
    ) {
      setFormData((prev) => ({
        ...prev,
        [name as keyof InformationFormData]: value,
      }))
    } else {
      //   console.warn(`[InformationForm] Attempted to set an unknown field: ${name}`)
    }
  }

  // Define a more precise type for dataToSave
  interface DataToSave {
    userType: string;
    contactEmail?: string;
    countryOfResidence?: string;
    purposeOfUse?: string;
    taxId?: string;
    personalInfo?: Partial<
      Omit<
        InformationFormData,
        "contactEmail" | "countryOfResidence" | "purposeOfUse" | "taxId"
      >
    >;
    businessInfo?: Partial<
      Omit<
        InformationFormData,
        "contactEmail" | "countryOfResidence" | "purposeOfUse" | "taxId"
      >
    >;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    const {
      contactEmail,
      countryOfResidence,
      purposeOfUse,
      taxId,
      ...specificProfileData
    } = formData

    let dataToSave: DataToSave // Use the more precise type

    if (userType === "INDIVIDUAL") {
      updateUserData({ personalInfo: formData, userType })
      dataToSave = {
        userType,
        contactEmail,
        countryOfResidence,
        purposeOfUse,
        taxId,
        personalInfo: specificProfileData,
      }
    } else if (userType === "BUSINESS") {
      updateUserData({ businessInfo: formData, userType })
      dataToSave = {
        userType,
        contactEmail,
        countryOfResidence,
        purposeOfUse,
        taxId,
        businessInfo: specificProfileData,
      }
    } else {
      //  console.error("Unknown user type in InformationForm")
      setIsSubmitting(false)
      return
    }

    try {
      //  console.log("Submitting onboarding info:", dataToSave)
      await saveOnboardingInfo(dataToSave)
      onNext()
    } catch (error) {
      //   console.error("Failed to save onboarding information:", error)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Modified commonFields to be a function that accepts props
  const CommonFieldsComponent = ({
    disabled,
    formData,
    userEmail,
    userType,
  }: {
    disabled?: boolean;
    formData: InformationFormData;
    userEmail?: string;
    userType: string;
  }) => {
    const purposeOptions =
      userType === "INDIVIDUAL"
        ? individualPurposeOfUseOptions
        : businessPurposeOfUseOptions
    const isFieldDisabled = disabled || isSubmitting || isLoading

    return (
      <>
        <div className="mb-4">
          <label
            htmlFor="contactEmail"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Contact Email
          </label>
          <input
            type="email"
            name="contactEmail"
            id="contactEmail"
            value={formData.contactEmail || userEmail || ""}
            onChange={handleChange}
            readOnly
            disabled={isFieldDisabled}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-gray-100 h-12 px-3"
          />
          <p className="mt-1 text-xs text-gray-500">
            This email was used for signup and will be used for KYC.
          </p>
        </div>
        <div className="mb-4">
          <label
            htmlFor="countryOfResidence"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Country of Residence
          </label>
          <Select
            value={formData.countryOfResidence || ""}
            onValueChange={(value) =>
              handleSelectChange("countryOfResidence", value)
            }
            disabled={isFieldDisabled}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select your country" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>Countries</SelectLabel>
                {countries.map((country) => (
                  <SelectItem key={country.value} value={country.value}>
                    {country.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        <div className="mb-4">
          <label
            htmlFor="purposeOfUse"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Purpose of Use
          </label>
          <Select
            value={formData.purposeOfUse || ""}
            onValueChange={(value) => handleSelectChange("purposeOfUse", value)}
            disabled={isFieldDisabled}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select purpose of use" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel>Purposes</SelectLabel>
                {purposeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        <div className="mb-4">
          <label
            htmlFor="taxId"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Tax Identification Number (Optional)
          </label>
          <input
            type="text"
            name="taxId"
            id="taxId"
            value={formData.taxId || ""}
            onChange={handleChange}
            disabled={isFieldDisabled}
            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm h-12 px-3"
          />
        </div>
      </>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 mb-6">
      {/* Title changed to not include (userType) */}
      {/* <h2 className="text-xl font-semibold text-gray-800">Your Information ({userType})</h2> */}
      {/* The main OnboardingScreen now displays the step title, so this header might be redundant or styled differently if kept */}

      {userType === "INDIVIDUAL" && (
        <>
          <div className="mb-4">
            <label
              htmlFor="firstName"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              First Name
            </label>
            <input
              type="text"
              name="firstName"
              id="firstName"
              value={formData.firstName || ""}
              onChange={handleChange}
              required
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm h-12 px-3"
              disabled={isSubmitting || isLoading}
            />
          </div>
          <div className="mb-4">
            <label
              htmlFor="lastName"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Last Name
            </label>
            <input
              type="text"
              name="lastName"
              id="lastName"
              value={formData.lastName || ""}
              onChange={handleChange}
              required
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm h-12 px-3"
              disabled={isSubmitting || isLoading}
            />
          </div>
          <CommonFieldsComponent
            disabled={isSubmitting || isLoading}
            formData={formData}
            userEmail={user?.email}
            userType={userType}
          />
        </>
      )}

      {userType === "BUSINESS" && (
        <>
          <div className="mb-4">
            <label
              htmlFor="businessName"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Business Name
            </label>
            <input
              type="text"
              name="businessName"
              id="businessName"
              value={formData.businessName || ""}
              onChange={handleChange}
              required
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm h-12 px-3"
              disabled={isSubmitting || isLoading}
            />
          </div>
          <div className="mb-4">
            <label
              htmlFor="businessEntityType"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Business Entity Type
            </label>
            <Select
              value={formData.businessEntityType || ""}
              onValueChange={(value) =>
                handleSelectChange("businessEntityType", value)
              }
              disabled={isSubmitting || isLoading}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select business entity type" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Entity Types</SelectLabel>
                  {businessEntityTypeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <div className="mb-4">
            <label
              htmlFor="contactName"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Contact Person Name
            </label>
            <input
              type="text"
              name="contactName"
              id="contactName"
              value={formData.contactName || ""}
              onChange={handleChange}
              required
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm h-12 px-3"
              disabled={isSubmitting || isLoading}
            />
          </div>
          <CommonFieldsComponent
            disabled={isSubmitting || isLoading}
            formData={formData}
            userEmail={user?.email}
            userType={userType}
          />
          <div className="mb-4">
            <label
              htmlFor="websiteUrl"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Website or Social Media (Optional)
            </label>
            <input
              type="url"
              name="websiteUrl"
              id="websiteUrl"
              value={formData.websiteUrl || ""}
              onChange={handleChange}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm h-12 px-3"
              disabled={isSubmitting || isLoading}
            />
          </div>
        </>
      )}

      <Button
        type="submit"
        className="w-full h-12"
        data-testid="info-form-submit-button"
        disabled={isSubmitting || isLoading}
      >
        {isSubmitting || isLoading ? "Saving..." : "Save and Continue"}
      </Button>
    </form>
  )
}
